import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useActiveTrials } from "@/hooks/useActiveTrials";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { VisitCadenceEditor, VisitCadenceItem } from "./VisitCadenceEditor";
import { ClaimTrialDialog } from "./ClaimTrialDialog";
import { 
  FileText, Calendar, ClipboardList, Receipt, Building2, 
  Search, Plus, Trash2, CheckCircle2, ArrowRight, ArrowLeft,
  Upload, Sparkles, Loader2
} from "lucide-react";
import { extractDocumentText } from "@/lib/regulatory/elsa/extractDocumentText";
import { extractTrialMetadataInChunks } from "@/lib/trial-site/chunkedTrialExtraction";

interface TrialOnboardingWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteOrgId: string;
  trialSiteId?: string;
  onTrialCreated?: (trialId: string, trialTitle: string) => void;
}

const STEPS = [
  { id: 'sponsor', label: 'Sponsor & eTMF', icon: Building2 },
  { id: 'details', label: 'Trial Details', icon: FileText },
  { id: 'visits', label: 'Visit Cadence', icon: Calendar },
  { id: 'criteria', label: 'I/E Criteria', icon: ClipboardList },
  { id: 'billing', label: 'Billing Setup', icon: Receipt },
];

export function TrialOnboardingWizard({ 
  open, onOpenChange, siteOrgId, trialSiteId, onTrialCreated 
}: TrialOnboardingWizardProps) {
  const { createActiveTrial, isCreating } = useActiveTrials(siteOrgId);
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [visitSchedule, setVisitSchedule] = useState<VisitCadenceItem[]>([]);
  const [inclusionCriteria, setInclusionCriteria] = useState<string[]>(['']);
  const [exclusionCriteria, setExclusionCriteria] = useState<string[]>(['']);

  // Sponsor/eTMF state
  const [sponsorData, setSponsorData] = useState({
    sponsor_name: "",
    sponsor_contact: "",
    etmf_reference: "",
    etmf_notes: "",
  });

  // AI extraction state
  const [extracting, setExtracting] = useState(false);
  const [extractedDocs, setExtractedDocs] = useState<Array<{
    filename: string;
    file: File;
    text: string;
    doc_type_guess: string;
    confidence?: any;
  }>>([]);
  const [ieSource, setIeSource] = useState<{ inclusion?: number; exclusion?: number; visits?: number } | null>(null);

  // Trial details state
  const [formData, setFormData] = useState({
    title: "",
    nct_id: "",
    phase: "",
    target_enrollment: "",
    protocol_version: "",
    start_date: "",
    expected_completion_date: "",
    contract_value: "",
    enrollment_target: "",
  });

  // Billing state
  const [billingData, setBillingData] = useState({
    screening_fee: "",
    enrollment_fee: "",
    per_visit_fee: "",
    completion_fee: "",
    currency: "USD",
  });

  // Fetch linked Veeva documents for context
  const { data: veevaDocuments } = useQuery({
    queryKey: ['veeva-etmf-docs', siteOrgId],
    queryFn: async () => {
      const { data } = await supabase
        .from('veeva_etmf_documents')
        .select('id, document_name, document_type, status')
        .limit(10)
        .order('created_at', { ascending: false });
      return data || [];
    },
    enabled: open,
  });

  const handleSubmit = async () => {
    createActiveTrial({
      trial: {
        title: formData.title,
        nct_id: formData.nct_id || undefined,
        sponsor_name: sponsorData.sponsor_name || undefined,
        phase: formData.phase || undefined,
        status: 'enrolling',
        target_enrollment: formData.target_enrollment ? parseInt(formData.target_enrollment) : undefined,
        protocol_version: formData.protocol_version || undefined,
        start_date: formData.start_date || undefined,
        expected_completion_date: formData.expected_completion_date || undefined,
        contract_value: formData.contract_value ? parseFloat(formData.contract_value) : undefined,
      },
      siteOrgId,
      enrollmentTarget: formData.enrollment_target ? parseInt(formData.enrollment_target) : undefined,
    }, {
      onSuccess: async (data) => {
        const trialId = data?.trialData?.id;

        if (trialId) {
          // Save protocol with visits and criteria
          const visitsWithNumbers = visitSchedule.map((v, idx) => ({
            ...v,
            visit_number: idx + 1,
          }));
          const filteredInclusion = inclusionCriteria.filter(c => c.trim());
          const filteredExclusion = exclusionCriteria.filter(c => c.trim());

          await supabase.from('trial_protocols').insert({
            trial_id: trialId,
            visit_schedule: visitSchedule.length > 0 ? { visits: visitsWithNumbers } : null,
            protocol_version: formData.protocol_version || 'v1.0',
            inclusion_criteria: { items: filteredInclusion },
            exclusion_criteria: { items: filteredExclusion },
          });

          // Save billing fee schedules if provided
          const feeSchedules = [];
          if (billingData.screening_fee) {
            feeSchedules.push({
              trial_id: trialId,
              trial_site_id: trialSiteId,
              event_type: 'screening',
              fee_amount: parseFloat(billingData.screening_fee),
              currency: billingData.currency,
              is_active: true,
            });
          }
          if (billingData.enrollment_fee) {
            feeSchedules.push({
              trial_id: trialId,
              trial_site_id: trialSiteId,
              event_type: 'enrollment_milestone',
              fee_amount: parseFloat(billingData.enrollment_fee),
              currency: billingData.currency,
              is_active: true,
            });
          }
          if (billingData.per_visit_fee) {
            feeSchedules.push({
              trial_id: trialId,
              trial_site_id: trialSiteId,
              event_type: 'patient_visit',
              fee_amount: parseFloat(billingData.per_visit_fee),
              currency: billingData.currency,
              is_active: true,
            });
          }
          if (billingData.completion_fee) {
            feeSchedules.push({
              trial_id: trialId,
              trial_site_id: trialSiteId,
              event_type: 'completion_milestone',
              fee_amount: parseFloat(billingData.completion_fee),
              currency: billingData.currency,
              is_active: true,
            });
          }

          if (feeSchedules.length > 0) {
            await supabase.from('protocol_fee_schedules').insert(feeSchedules);
          }

          // Auto-file uploaded sponsor documents into the eTMF for this site
          if (trialSiteId) {
            await autoFileToTMF(trialSiteId, trialId);
          }

          toast({
            title: "Trial Onboarded Successfully",
            description: `${formData.title} is now fully configured with protocol, visits, criteria, billing${extractedDocs.length ? `, and ${extractedDocs.length} eTMF document(s) auto-filed` : ''}.`,
          });
        }

        // Reset
        onOpenChange(false);
        setCurrentStep(0);
        setSponsorData({ sponsor_name: "", sponsor_contact: "", etmf_reference: "", etmf_notes: "" });
        setFormData({ title: "", nct_id: "", phase: "", target_enrollment: "", protocol_version: "", start_date: "", expected_completion_date: "", contract_value: "", enrollment_target: "" });
        setBillingData({ screening_fee: "", enrollment_fee: "", per_visit_fee: "", completion_fee: "", currency: "USD" });
        setVisitSchedule([]);
        setInclusionCriteria(['']);
        setExclusionCriteria(['']);
        setExtractedDocs([]);
        setIeSource(null);

        if (trialId) {
          onTrialCreated?.(trialId, formData.title);
        }
      }
    });
  };

  const canProceed = () => {
    if (currentStep === 1) return !!formData.title;
    return true;
  };

  const handleDocumentUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setExtracting(true);
    try {
      const newDocs: typeof extractedDocs = [];
      const aggregated: any = { inclusion: [], exclusion: [], visits: [] };
      for (const file of Array.from(files)) {
        toast({ title: "Reading document", description: file.name });
        const { text } = await extractDocumentText(file);
        if (!text || text.length < 200) {
          toast({ title: "Skipped", description: `${file.name}: no extractable text.`, variant: "destructive" });
          continue;
        }
        const ex = await extractTrialMetadataInChunks(supabase as any, text, file.name);
        newDocs.push({
          filename: file.name,
          file,
          text: text.slice(0, 500),
          doc_type_guess: ex.document_type_guess || "other",
          confidence: ex.confidence,
        });

        // Merge into form fields — only fill blanks, never overwrite user input
        setSponsorData((prev) => ({
          ...prev,
          sponsor_name: prev.sponsor_name || ex.sponsor_name || "",
          sponsor_contact: prev.sponsor_contact || ex.sponsor_contact_email || "",
        }));
        setFormData((prev) => ({
          ...prev,
          title: prev.title || ex.title || "",
          nct_id: prev.nct_id || ex.nct_id || "",
          phase: prev.phase || ex.phase || "",
          protocol_version: prev.protocol_version || ex.protocol_version || "",
          target_enrollment: prev.target_enrollment || (ex.target_enrollment ? String(ex.target_enrollment) : ""),
        }));
        if (Array.isArray(ex.inclusion_criteria)) aggregated.inclusion.push(...ex.inclusion_criteria);
        if (Array.isArray(ex.exclusion_criteria)) aggregated.exclusion.push(...ex.exclusion_criteria);
        if (Array.isArray(ex.visit_schedule)) aggregated.visits.push(...ex.visit_schedule);
      }

      if (aggregated.inclusion.length > 0 && inclusionCriteria.filter((c) => c.trim()).length === 0) {
        setInclusionCriteria(aggregated.inclusion);
      }
      if (aggregated.exclusion.length > 0 && exclusionCriteria.filter((c) => c.trim()).length === 0) {
        setExclusionCriteria(aggregated.exclusion);
      }
      if (aggregated.visits.length > 0 && visitSchedule.length === 0) {
        setVisitSchedule(
          aggregated.visits.map((v: any, i: number) => ({
            id: `ai-${Date.now()}-${i}`,
            name: v.visit_name || `Visit ${i + 1}`,
            type: (v.type || "screening").toLowerCase(),
            days_from_enrollment: typeof v.day === "number" ? v.day : i * 14,
            window_before: typeof v.window_days === "number" ? v.window_days : 3,
            window_after: typeof v.window_days === "number" ? v.window_days : 3,
            billable: true,
          })),
        );
      }
      setIeSource({
        inclusion: aggregated.inclusion.length,
        exclusion: aggregated.exclusion.length,
        visits: aggregated.visits.length,
      });
      setExtractedDocs((prev) => [...prev, ...newDocs]);
      toast({
        title: "Extraction complete",
        description: `${newDocs.length} document(s) parsed. Review pre-filled fields below.`,
      });
    } catch (e: any) {
      toast({ title: "Extraction error", description: e.message, variant: "destructive" });
    } finally {
      setExtracting(false);
    }
  };

  const autoFileToTMF = async (trialSiteIdForFile: string, trialId: string) => {
    if (!trialSiteIdForFile || extractedDocs.length === 0) return;
    const zoneMap: Record<string, { zone: string; section: string; artifact: string; category: string }> = {
      protocol:              { zone: "02", section: "02.01", artifact: "Protocol",             category: "central_trial" },
      investigator_brochure: { zone: "02", section: "02.03", artifact: "Investigator Brochure", category: "central_trial" },
      icf:                   { zone: "02", section: "02.04", artifact: "Informed Consent Form", category: "central_trial" },
      lab_manual:            { zone: "06", section: "06.02", artifact: "Lab Manual",            category: "central_local_testing" },
      ip_manual:             { zone: "08", section: "08.01", artifact: "IP Manual / Pharmacy",  category: "ip_and_trial_supplies" },
      other:                 { zone: "01", section: "01.02", artifact: "Trial Master File Plan", category: "trial_management" },
    };
    for (const doc of extractedDocs) {
      const map = zoneMap[doc.doc_type_guess] || zoneMap.other;
      try {
        // Upload file to storage
        const path = `${trialSiteIdForFile}/${trialId}/${Date.now()}-${doc.file.name}`;
        const { error: upErr } = await supabase.storage
          .from("etmf-documents")
          .upload(path, doc.file, { upsert: false });
        if (upErr) throw upErr;
        await supabase.from("etmf_documents").insert({
          trial_site_id: trialSiteIdForFile,
          document_category: map.category,
          document_type: doc.doc_type_guess,
          document_name: doc.file.name,
          file_url: path,
          status: "pending_review",
          tmf_zone: map.zone,
          tmf_section: map.section,
          tmf_artifact: map.artifact,
          auto_filed: true,
          source_module: "trial_onboarding_wizard",
          is_essential: true,
        } as any);
      } catch (e) {
        console.error("[TMF auto-file]", doc.file.name, e);
      }
    }
  };



  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Trial Onboarding Wizard</DialogTitle>
          <DialogDescription>
            Complete guided setup: Sponsor eTMF → Trial Details → Visits → Criteria → Billing
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-1 overflow-x-auto pb-2">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isComplete = index < currentStep;
            return (
              <div key={step.id} className="flex items-center">
                <button
                  onClick={() => setCurrentStep(index)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap",
                    isActive && "bg-primary text-primary-foreground",
                    isComplete && "bg-primary/20 text-primary",
                    !isActive && !isComplete && "bg-muted text-muted-foreground"
                  )}
                >
                  {isComplete ? <CheckCircle2 className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                  <span className="hidden sm:inline">{step.label}</span>
                </button>
                {index < STEPS.length - 1 && <ArrowRight className="h-3 w-3 text-muted-foreground mx-1 shrink-0" />}
              </div>
            );
          })}
        </div>

        {/* Step 0: Sponsor & eTMF */}
        {currentStep === 0 && (
          <div className="space-y-4">
            <div className="p-3 border rounded-lg bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900 flex items-start justify-between gap-3">
              <p className="text-xs text-blue-900 dark:text-blue-200">
                <strong>Site-led trial creation:</strong> create the trial shell first, then upload the sponsor pack.
                The trial belongs to the sponsor — your site is recorded on the trial's site list, never as sponsor or CRO.
              </p>
              <a
                href="/trial-site/sops#site-new-trial-doc-driven"
                target="_blank"
                rel="noreferrer"
                className="text-xs font-medium text-blue-700 dark:text-blue-300 underline whitespace-nowrap shrink-0"
              >
                View SOP
              </a>
            </div>
            <div className="p-4 border rounded-lg bg-muted/50">
              <div className="flex items-center justify-between mb-3">
                <p className="font-medium text-sm">Link to existing trial?</p>
                <Button variant="outline" size="sm" onClick={() => setShowClaimDialog(true)}>
                  <Search className="h-4 w-4 mr-2" />
                  Claim Trial
                </Button>
              </div>
            </div>

            {/* AI Document Auto-fill */}
            <div className="p-4 border-2 border-primary/30 rounded-lg bg-primary/5 space-y-3">
              <div className="flex items-start gap-2">
                <Sparkles className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-medium text-sm">AI Auto-fill from Sponsor Documents</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Upload the Protocol, Lab Manual, IP Manual, or Investigator Brochure (PDF / DOCX).
                    We extract sponsor identity, protocol identifiers, visit schedule, and I/E criteria — you review and approve.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.docx,.txt"
                    multiple
                    disabled={extracting}
                    onChange={(e) => handleDocumentUpload(e.target.files)}
                  />
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium border rounded-md bg-background hover:bg-muted transition">
                    {extracting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    {extracting ? "Extracting…" : "Upload sponsor documents"}
                  </span>
                </label>
                {extractedDocs.length > 0 && (
                  <Badge variant="secondary" className="text-[10px]">
                    {extractedDocs.length} document(s) parsed
                  </Badge>
                )}
              </div>
              {extractedDocs.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {extractedDocs.map((d, idx) => (
                    <div key={idx} className="flex items-center justify-between p-1.5 border rounded text-xs bg-background">
                      <span className="truncate flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3 text-green-600 shrink-0" />
                        {d.filename}
                      </span>
                      <Badge variant="outline" className="text-[10px]">{d.doc_type_guess}</Badge>
                    </div>
                  ))}
                </div>
              )}
              {ieSource && (ieSource.inclusion || ieSource.exclusion || ieSource.visits) ? (
                <p className="text-[11px] text-muted-foreground">
                  Auto-populated: {ieSource.inclusion || 0} inclusion, {ieSource.exclusion || 0} exclusion criteria, {ieSource.visits || 0} visits. Review on subsequent steps.
                </p>
              ) : null}
            </div>


            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="col-span-1 sm:col-span-2">
                <Label>Sponsor Name</Label>
                <Input
                  value={sponsorData.sponsor_name}
                  onChange={(e) => setSponsorData({ ...sponsorData, sponsor_name: e.target.value })}
                  placeholder="Pharma Company Inc"
                />
              </div>
              <div>
                <Label>Sponsor Contact</Label>
                <Input
                  value={sponsorData.sponsor_contact}
                  onChange={(e) => setSponsorData({ ...sponsorData, sponsor_contact: e.target.value })}
                  placeholder="contact@sponsor.com"
                />
              </div>
              <div>
                <Label>eTMF Reference ID</Label>
                <Input
                  value={sponsorData.etmf_reference}
                  onChange={(e) => setSponsorData({ ...sponsorData, etmf_reference: e.target.value })}
                  placeholder="VEEVA-DOC-12345"
                />
              </div>
              <div className="col-span-1 sm:col-span-2">
                <Label>eTMF Notes</Label>
                <Textarea
                  value={sponsorData.etmf_notes}
                  onChange={(e) => setSponsorData({ ...sponsorData, etmf_notes: e.target.value })}
                  placeholder="Notes about received documents..."
                  rows={2}
                />
              </div>
            </div>

            {veevaDocuments && veevaDocuments.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Recent Veeva eTMF Documents</Label>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {veevaDocuments.map((doc: any) => (
                    <div key={doc.id} className="flex items-center justify-between p-2 border rounded text-xs">
                      <span className="truncate">{doc.document_name}</span>
                      <Badge variant="outline" className="text-[10px]">{doc.document_type}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 1: Trial Details */}
        {currentStep === 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="col-span-1 sm:col-span-2">
              <Label>Trial Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Study of Drug X for Condition Y"
                required
              />
            </div>
            <div>
              <Label>NCT ID</Label>
              <Input
                value={formData.nct_id}
                onChange={(e) => setFormData({ ...formData, nct_id: e.target.value })}
                placeholder="NCT12345678"
              />
            </div>
            <div>
              <Label>Phase</Label>
              <Select value={formData.phase} onValueChange={(v) => setFormData({ ...formData, phase: v })}>
                <SelectTrigger><SelectValue placeholder="Select phase" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Phase 1">Phase 1</SelectItem>
                  <SelectItem value="Phase 2">Phase 2</SelectItem>
                  <SelectItem value="Phase 3">Phase 3</SelectItem>
                  <SelectItem value="Phase 4">Phase 4</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Protocol Version</Label>
              <Input value={formData.protocol_version} onChange={(e) => setFormData({ ...formData, protocol_version: e.target.value })} placeholder="v1.0" />
            </div>
            <div>
              <Label>Target Enrollment (Total)</Label>
              <Input type="number" value={formData.target_enrollment} onChange={(e) => setFormData({ ...formData, target_enrollment: e.target.value })} placeholder="100" />
            </div>
            <div>
              <Label>Site Enrollment Target</Label>
              <Input type="number" value={formData.enrollment_target} onChange={(e) => setFormData({ ...formData, enrollment_target: e.target.value })} placeholder="20" />
            </div>
            <div>
              <Label>Start Date</Label>
              <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} />
            </div>
            <div>
              <Label>Expected Completion</Label>
              <Input type="date" value={formData.expected_completion_date} onChange={(e) => setFormData({ ...formData, expected_completion_date: e.target.value })} />
            </div>
            <div className="col-span-1 sm:col-span-2">
              <Label>Contract Value (USD)</Label>
              <Input type="number" step="0.01" value={formData.contract_value} onChange={(e) => setFormData({ ...formData, contract_value: e.target.value })} placeholder="100000.00" />
            </div>
          </div>
        )}

        {/* Step 2: Visit Cadence */}
        {currentStep === 2 && (
          <VisitCadenceEditor visits={visitSchedule} onChange={setVisitSchedule} />
        )}

        {/* Step 3: I/E Criteria */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Define eligibility criteria for patient screening.</p>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Badge variant="default" className="bg-green-600">Inclusion</Badge>
                Criteria
              </Label>
              {inclusionCriteria.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={item}
                    onChange={(e) => {
                      const updated = [...inclusionCriteria];
                      updated[index] = e.target.value;
                      setInclusionCriteria(updated);
                    }}
                    placeholder="e.g., Age ≥18 years"
                  />
                  <Button type="button" variant="ghost" size="icon" onClick={() => setInclusionCriteria(inclusionCriteria.filter((_, i) => i !== index))} disabled={inclusionCriteria.length === 1}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setInclusionCriteria([...inclusionCriteria, ''])}>
                <Plus className="h-4 w-4 mr-1" /> Add Inclusion
              </Button>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Badge variant="destructive">Exclusion</Badge>
                Criteria
              </Label>
              {exclusionCriteria.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={item}
                    onChange={(e) => {
                      const updated = [...exclusionCriteria];
                      updated[index] = e.target.value;
                      setExclusionCriteria(updated);
                    }}
                    placeholder="e.g., Pregnant or nursing"
                  />
                  <Button type="button" variant="ghost" size="icon" onClick={() => setExclusionCriteria(exclusionCriteria.filter((_, i) => i !== index))} disabled={exclusionCriteria.length === 1}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setExclusionCriteria([...exclusionCriteria, ''])}>
                <Plus className="h-4 w-4 mr-1" /> Add Exclusion
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Billing Setup */}
        {currentStep === 4 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Set per-patient fee schedule for this trial. These will auto-trigger billing events when patients reach milestones.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Screening Fee</Label>
                <Input type="number" step="0.01" value={billingData.screening_fee} onChange={(e) => setBillingData({ ...billingData, screening_fee: e.target.value })} placeholder="500.00" />
              </div>
              <div>
                <Label>Enrollment Fee</Label>
                <Input type="number" step="0.01" value={billingData.enrollment_fee} onChange={(e) => setBillingData({ ...billingData, enrollment_fee: e.target.value })} placeholder="2000.00" />
              </div>
              <div>
                <Label>Per-Visit Fee</Label>
                <Input type="number" step="0.01" value={billingData.per_visit_fee} onChange={(e) => setBillingData({ ...billingData, per_visit_fee: e.target.value })} placeholder="300.00" />
              </div>
              <div>
                <Label>Completion Fee</Label>
                <Input type="number" step="0.01" value={billingData.completion_fee} onChange={(e) => setBillingData({ ...billingData, completion_fee: e.target.value })} placeholder="1500.00" />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={billingData.currency} onValueChange={(v) => setBillingData({ ...billingData, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="ZAR">ZAR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Summary */}
            <Card className="border-primary/20">
              <CardContent className="p-4">
                <p className="text-sm font-medium mb-2">Onboarding Summary</p>
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <span>Sponsor: {sponsorData.sponsor_name || '—'}</span>
                  <span>Trial: {formData.title || '—'}</span>
                  <span>Visits: {visitSchedule.length} scheduled</span>
                  <span>Inclusion: {inclusionCriteria.filter(c => c.trim()).length} criteria</span>
                  <span>Exclusion: {exclusionCriteria.filter(c => c.trim()).length} criteria</span>
                  <span>Fee schedules: {[billingData.screening_fee, billingData.enrollment_fee, billingData.per_visit_fee, billingData.completion_fee].filter(Boolean).length} configured</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => currentStep === 0 ? onOpenChange(false) : setCurrentStep(currentStep - 1)}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </Button>

          {currentStep < STEPS.length - 1 ? (
            <Button
              type="button"
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!canProceed()}
            >
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isCreating || !formData.title}>
              {isCreating ? "Creating..." : "Complete Onboarding"}
            </Button>
          )}
        </div>
      </DialogContent>

      <ClaimTrialDialog
        open={showClaimDialog}
        onOpenChange={setShowClaimDialog}
        siteOrgId={siteOrgId}
        onTrialClaimed={(trialId) => {
          onTrialCreated?.(trialId, "Claimed Trial");
          onOpenChange(false);
        }}
      />
    </Dialog>
  );
}
